'use strict';

import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {Observer} from 'gsap/Observer';

gsap.registerPlugin(ScrollTrigger, Observer);

const $ = jQuery.noConflict();
$(function() {

	const scrollAcc = {

		// Tuning knobs. The two that scale with the viewport are further down as
		// functions, since they have to be re-read whenever the window changes.
		cooldown: 800,
		inputWindow: 500,
		animDuration: 0.6,
		animEase: 'power3.inOut',

		activeIndex: 0,
		bodyHeights: [],
		headerHeights: [],
		stickyInset: 0,
		scrollTrigger: null,
		observer: null,
		transition: null,
		isTransitioning: false,
		inputDelta: 0,
		lastInputTime: 0,
		cooldownUntil: 0,
		released: 0,
		lastProgress: 0,
		resizeTimer: null,
		onResizeBound: null,
		motionQuery: null,
		isStaticMode: false,
		staticTweens: [],

		init: function() {
			this.outer = document.querySelector('.js-scroll-acc');
			if (!this.outer) return;

			this.sticky = this.outer.querySelector('.js-scroll-acc-sticky');
			this.panels = [...this.outer.querySelectorAll('.js-scroll-acc-panel')];
			this.bodies = [...this.outer.querySelectorAll('.js-scroll-acc-body')];
			this.headers = this.panels.map((panel) => panel.querySelector('.scroll-acc__header'));
			this.contents = this.panels.map((panel) => panel.querySelector('.scroll-acc__content'));
			this.total = this.panels.length;

			if (this.total < 2) return;

			this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
			this.bindListeners();

			if (this.isStatic()) {
				this.setupStatic();
				return;
			}

			this.waitForImages().then(() => {
				if (this.isStatic()) {
					this.setupStatic();
				} else {
					this.setupDesktop();
				}
			});
		},

		bindListeners: function() {
			if (this.onResizeBound) return;

			this.onResizeBound = this.onResize.bind(this);
			window.addEventListener('resize', this.onResizeBound);

			if (this.motionQuery.addEventListener) {
				this.motionQuery.addEventListener('change', this.onResizeBound);
			}
		},

		waitForImages: function() {
			const images = [...this.outer.querySelectorAll('img')];
			const timeout = new Promise((resolve) => {
				setTimeout(resolve, 2000);
			});

			const loaded = Promise.all(images.map((img) => {
				if (img.complete && img.naturalHeight > 0) {
					return Promise.resolve();
				}

				return new Promise((resolve) => {
					img.addEventListener('load', resolve, {once: true});
					img.addEventListener('error', resolve, {once: true});
				});
			}));

			return Promise.race([loaded, timeout]);
		},

		getHeaderOffset: function() {
			const header = document.querySelector('.site-header');
			return header ? Math.max(header.offsetHeight - 46, 50) : 60;
		},

		isMobile: function() {
			return window.matchMedia('(max-width: 991px)').matches;
		},

		prefersReducedMotion: function() {
			return !!(this.motionQuery && this.motionQuery.matches);
		},

		// Static mode = plain stacked cards, no pinning and no open/close animation.
		isStatic: function() {
			return this.isMobile() || this.prefersReducedMotion();
		},

		// How much accumulated scroll movement opens the next card - 15% of the
		// viewport, the light end of the band the client asked for. Measuring in pixels
		// rather than in wheel events is deliberate: a trackpad fires dozens of tiny
		// deltas where a mouse wheel fires a handful of large ones, and only the pixel
		// total is comparable between the two.
		getInputThreshold: function() {
			return Math.max(110, window.innerHeight * 0.15);
		},

		// Scroll runway spent per card. Deliberately much longer than the input
		// threshold and deliberately not tied to it: nothing is driven by scroll
		// position while the Observer has the input, so this only sets how long the
		// pin is. A short pin is what lets a fast flick clear the whole section in a
		// single scroll step before the Observer ever gets a chance to take over.
		getStepDistance: function() {
			return Math.max(300, window.innerHeight * 0.35);
		},

		// Free scroll kept before the first card and after the last one. Once an end
		// card has been read, one more deliberate gesture releases the section and the
		// user scrolls out of it natively through this runway.
		getEdgeDistance: function() {
			return Math.max(400, window.innerHeight * 0.6);
		},

		getScrollDistance: function() {
			return ((this.total - 1) * this.getStepDistance()) + (this.getEdgeDistance() * 2);
		},

		// Scroll position that corresponds to a card sitting open.
		getScrollForIndex: function(index) {
			if (!this.scrollTrigger) return 0;

			return this.scrollTrigger.start + this.getEdgeDistance() + (index * this.getStepDistance());
		},

		getIndexFromScroll: function(scroll) {
			if (!this.scrollTrigger) return 0;

			const raw = Math.round((scroll - this.scrollTrigger.start - this.getEdgeDistance()) / this.getStepDistance());

			return Math.min(this.total - 1, Math.max(0, raw));
		},

		// Height the pinned card stack has to fill: the viewport minus everything the
		// pin pushes it down by (site header, the section's own top padding) and minus
		// the sticky's own padding. stickyInset is cached because it can only be read
		// while the sticky still sits in flow - the pin takes it out of the document.
		getAvailableHeight: function() {
			return window.innerHeight - this.getHeaderOffset() - this.stickyInset;
		},

		// Open size of a card. Only one card is ever on screen, so every one of them
		// gets the whole pinned area: its header at the top and its body filling the
		// rest. That is what keeps the first card from being the cramped one - it no
		// longer has to share the viewport with the two headers below it.
		//
		// Any room beyond the content is split above and below it, as a transform
		// rather than a margin: the body drops its BFC when it switches to
		// overflow:visible at the end of the tween, and a margin would collapse out of
		// it at that moment and grow the whole card by the offset.
		getPanelMetrics: function(index) {
			const natural = this.bodyHeights[index];
			const body = Math.max(natural, Math.round(this.getAvailableHeight() - this.headerHeights[index]));

			return {
				body,
				offset: Math.round((body - natural) / 2),
				panel: this.headerHeights[index] + body,
			};
		},

		// Every number the animation runs on, read in one pass while the sticky is still
		// in flow - once the pin engages it is out of the document and its position
		// relative to the section can no longer be measured.
		measure: function() {
			// Cards are opened in place rather than out of flow, so each body is laid
			// out at its real column width - which is what its height depends on. The
			// content offset has to come off first: a transform counts towards the
			// scrollable overflow and would inflate the height being read.
			gsap.set(this.contents, {y: 0});

			this.bodyHeights = this.bodies.map((body) => {
				gsap.set(body, {height: 'auto'});

				return body.scrollHeight;
			});

			// Closed again in the same task, so the open state is never painted. No
			// opacity here - fading is the panel's job, and a body left at 0 would stay
			// invisible inside a perfectly visible panel.
			gsap.set(this.bodies, {height: 0, overflow: 'hidden'});
			gsap.set(this.panels, {clearProps: 'height,opacity,overflow'});

			const styles = window.getComputedStyle(this.sticky);

			this.stickyInset = Math.round(
				this.sticky.getBoundingClientRect().top - this.outer.getBoundingClientRect().top
				+ parseFloat(styles.paddingTop)
				+ parseFloat(styles.paddingBottom)
			);

			this.headerHeights = this.headers.map((header) => (
				header ? Math.round(header.getBoundingClientRect().height) : 0
			));
		},

		// Only the open card needs its insides laid out. The rest are zero-height and
		// clipped, so whatever their bodies still hold stays invisible until they open,
		// and they get sized fresh at that point.
		applyPanelStates: function() {
			const active = this.activeIndex;
			const metrics = this.getPanelMetrics(active);

			this.panels.forEach((panel, i) => {
				gsap.set(panel, i === active
					? {height: metrics.panel, opacity: 1, overflow: 'visible'}
					: {height: 0, opacity: 0, overflow: 'hidden'});
			});

			// Clipping is the panel's job, so an open body stays visible and the rotated
			// covers are free to bleed past its box the way they always did.
			gsap.set(this.bodies[active], {height: metrics.body, overflow: 'visible'});
			gsap.set(this.contents[active], {y: metrics.offset});
		},

		// Panels are the only things ever tweened - bodies and contents are positioned
		// with gsap.set, which leaves no tween behind to kill.
		killPanelTweens: function() {
			if (this.transition) {
				this.transition.kill();
				this.transition = null;
			}

			gsap.killTweensOf(this.panels);
		},

		resetState: function() {
			this.killPanelTweens();

			this.activeIndex = 0;
			this.isTransitioning = false;
			this.released = 0;
			this.lastProgress = 0;
			this.resetInput();
		},

		resetInput: function() {
			this.inputDelta = 0;
			this.lastInputTime = 0;
			this.cooldownUntil = 0;
		},

		// Parts of a card that get the simple entrance animation in static mode.
		getStaticTargets: function(panel) {
			return [
				panel.querySelector('.scroll-acc__header'),
				panel.querySelector('.scroll-acc__images'),
				panel.querySelector('.scroll-acc__text'),
			].filter(Boolean);
		},

		// Static mode drops the pinned open/close choreography, so each card gets a
		// plain zoom-in on scroll instead - same feel as the data-animate="zoom-in"
		// used elsewhere on the site.
		buildStaticAnimations: function() {
			this.killStaticAnimations();

			if (this.prefersReducedMotion()) {
				return;
			}

			this.panels.forEach((panel) => {
				const targets = this.getStaticTargets(panel);

				if (!targets.length) {
					return;
				}

				gsap.set(targets, {opacity: 0, scale: 0.94});

				this.staticTweens.push(gsap.to(targets, {
					opacity: 1,
					scale: 1,
					duration: 0.8,
					ease: 'power2.out',
					stagger: 0.12,
					clearProps: 'transform,opacity',
					scrollTrigger: {
						trigger: panel,
						start: 'top 85%',
						once: true,
					},
				}));
			});
		},

		killStaticAnimations: function() {
			this.staticTweens.forEach((tween) => {
				if (tween.scrollTrigger) {
					tween.scrollTrigger.kill();
				}

				tween.kill();
			});

			this.staticTweens = [];

			this.panels.forEach((panel) => {
				const targets = this.getStaticTargets(panel);

				if (targets.length) {
					gsap.set(targets, {clearProps: 'transform,opacity'});
				}
			});
		},

		setupDesktop: function() {
			this.isStaticMode = false;
			this.killStaticAnimations();

			// Unpin before measuring: while the pin is live the sticky is out of flow,
			// so its position relative to the section cannot be read.
			if (this.scrollTrigger) {
				this.scrollTrigger.kill();
				this.scrollTrigger = null;
			}

			this.resetState();

			this.measure();
			this.setIndexInstantly(0);

			this.outer.classList.add('is-ready', 'is-pinned');

			this.scrollTrigger = ScrollTrigger.create({
				trigger: this.outer,
				start: () => `top top+=${this.getHeaderOffset()}`,
				end: () => `+=${this.getScrollDistance()}`,
				pin: this.sticky,
				pinSpacing: true,
				anticipatePin: 1,
				invalidateOnRefresh: true,
				// Baseline for the fly-through check in onScroll. Without it a reload
				// that restores the scroll below the section reads as a jump from 0 to
				// 1 and yanks the user back up into it.
				onRefresh: (self) => {
					this.lastProgress = self.progress;
				},
				onUpdate: (self) => this.onScroll(self),
			});

			this.lastProgress = this.scrollTrigger.progress;

			this.createObserver();
		},

		// The Observer - not raw scroll position - decides when a card opens. Native
		// scrolling is held back while the stack is on screen, so a fast flick cannot
		// carry the page past a card before the user has seen it.
		createObserver: function() {
			if (this.observer) {
				this.observer.kill();
			}

			this.observer = Observer.create({
				target: window,
				type: 'wheel,touch',
				preventDefault: true,
				allowClicks: true,
				lockAxis: true,
				tolerance: 0,
				// Observer batches raw events per frame; onInput sums those frames into
				// the gesture total and owns the time window it accumulates over.
				onChangeY: (self) => {
					// Dragging is reported in pointer space, where moving down means
					// moving the content down - the opposite of a downward wheel.
					this.onInput(self.isDragging ? -self.deltaY : self.deltaY);
				},
			});

			this.observer.disable();
		},

		onInput: function(delta) {
			if (!this.observer || !this.observer.isEnabled || !delta) {
				return;
			}

			const now = performance.now();
			const idle = now - this.lastInputTime;

			this.lastInputTime = now;

			// Cooldown: keep eating input so the tail of a flick is spent, not queued.
			if (this.isTransitioning || now < this.cooldownUntil) {
				this.inputDelta = 0;
				return;
			}

			// The delta only accumulates across one continuous movement. The window has
			// to be wide enough to hold a mouse wheel together: a wheel fires one large
			// tick every 100-200ms where a trackpad fires tiny ones every frame, so a
			// tight window would quietly reset the wheel to zero between every tick and
			// the card would never open no matter how long the user scrolled.
			if (idle > this.inputWindow || (this.inputDelta && Math.sign(delta) !== Math.sign(this.inputDelta))) {
				this.inputDelta = 0;
			}

			this.inputDelta += delta;

			if (Math.abs(this.inputDelta) < this.getInputThreshold()) {
				return;
			}

			const direction = this.inputDelta > 0 ? 1 : -1;
			const next = this.activeIndex + direction;

			this.inputDelta = 0;
			this.cooldownUntil = now + this.cooldown;

			if (next < 0 || next > this.total - 1) {
				this.release(direction);
				return;
			}

			this.switchPanel(next);
		},

		// Past the first or last card there is nothing left to open, so hand the page
		// back to native scrolling and nudge into the edge runway. Both happen while
		// the stack is still pinned, so nothing moves on screen.
		release: function(direction) {
			if (!this.scrollTrigger) return;

			const edge = this.getEdgeDistance() * 0.5;
			const target = direction > 0
				? this.getScrollForIndex(this.total - 1) + edge
				: this.getScrollForIndex(0) - edge;

			this.released = direction;
			this.observer.disable();
			this.resetInput();
			this.scrollTrigger.scroll(target);
		},

		onScroll: function(self) {
			if (!this.observer) {
				return;
			}

			const prevProgress = this.lastProgress;

			this.lastProgress = self.progress;

			// A released section is handed back until the user turns around, at which
			// point they clearly want the cards again rather than to leave.
			if (this.released && self.direction !== this.released) {
				this.released = 0;
			}

			// One scroll step took the page clean over the pinned range without the
			// Observer ever getting the input - a hard flick, a jump to an anchor, or
			// scroll restoration. Pull back to the card they should have landed on
			// instead of letting the whole section go by unread.
			if (!this.released && !this.isTransitioning
				&& ((prevProgress <= 0 && self.progress >= 1) || (prevProgress >= 1 && self.progress <= 0))) {
				const entry = self.progress >= 1 ? 0 : this.total - 1;

				this.setIndexInstantly(entry);
				self.scroll(this.getScrollForIndex(entry));
				return;
			}

			const index = this.getIndexFromScroll(self.scroll());

			if (!self.isActive || this.released) {
				if (this.observer.isEnabled) {
					this.observer.disable();
					this.resetInput();
				}

				// Scrolled clear of the stack - catch up without animating.
				if (index !== this.activeIndex && !this.isTransitioning) {
					this.setIndexInstantly(index);
				}

				return;
			}

			// Entering the stack: take over input and settle on the card the scroll
			// position landed nearest to, cancelling any overshoot from the gesture.
			if (!this.observer.isEnabled) {
				this.observer.enable();
				this.resetInput();

				if (index !== this.activeIndex) {
					this.setIndexInstantly(index);
				}

				self.scroll(this.getScrollForIndex(index));
				return;
			}

			if (this.isTransitioning || index === this.activeIndex) {
				return;
			}

			// Keyboard, scrollbar drag or scroll restoration can still move the page
			// while the Observer only holds back wheel and touch.
			this.switchPanel(index > this.activeIndex ? this.activeIndex + 1 : this.activeIndex - 1);
		},

		switchPanel: function(newIndex) {
			if (newIndex === this.activeIndex || newIndex < 0 || newIndex > this.total - 1) {
				return;
			}

			const prevIndex = this.activeIndex;
			const metrics = this.getPanelMetrics(newIndex);

			this.killPanelTweens();

			this.panels[prevIndex].classList.add('scroll-acc__panel--collapsed');
			this.panels[newIndex].classList.remove('scroll-acc__panel--collapsed');
			this.activeIndex = newIndex;
			this.isTransitioning = true;

			// The opening card is laid out at its final size up front and then wiped in
			// by its own height, so the content never reflows mid-tween. Both cards run
			// the same ease over the same span, so the two heights always add up to one
			// full card and the stack never grows or collapses under the pin.
			gsap.set(this.panels[newIndex], {overflow: 'hidden'});
			gsap.set(this.bodies[newIndex], {height: metrics.body, overflow: 'visible'});
			gsap.set(this.contents[newIndex], {y: metrics.offset});

			this.transition = gsap.timeline({
				defaults: {duration: this.animDuration, ease: this.animEase},
				onComplete: () => {
					gsap.set(this.panels[newIndex], {overflow: 'visible'});
					this.isTransitioning = false;
					this.transition = null;
				},
			});

			this.transition
				.to(this.panels[prevIndex], {height: 0, opacity: 0, overflow: 'hidden'}, 0)
				.to(this.panels[newIndex], {height: metrics.panel, opacity: 1}, 0);

			if (this.scrollTrigger) {
				this.scrollTrigger.scroll(this.getScrollForIndex(newIndex));
			}
		},

		setIndexInstantly: function(index) {
			this.killPanelTweens();

			this.activeIndex = index;
			this.isTransitioning = false;

			this.panels.forEach((panel, i) => {
				panel.classList.toggle('scroll-acc__panel--collapsed', i !== index);
			});

			this.applyPanelStates();
		},

		setupStatic: function() {
			this.isStaticMode = true;

			if (this.scrollTrigger) {
				this.scrollTrigger.kill();
				this.scrollTrigger = null;
			}

			if (this.observer) {
				this.observer.kill();
				this.observer = null;
			}

			this.resetState();

			this.outer.classList.add('is-ready');
			this.outer.classList.remove('is-pinned');

			this.panels.forEach((panel) => {
				panel.classList.remove('scroll-acc__panel--collapsed');
			});

			gsap.set(this.contents, {clearProps: 'transform'});
			gsap.set([...this.panels, ...this.bodies], {clearProps: 'height,overflow,opacity'});

			this.buildStaticAnimations();
		},

		onResize: function() {
			clearTimeout(this.resizeTimer);

			this.resizeTimer = setTimeout(() => {
				// Already static and staying static: nothing to re-measure, and
				// rebuilding would replay the entrance animation on cards the user
				// has passed (mobile browsers resize on every URL-bar toggle).
				if (this.isStatic() && this.isStaticMode) {
					return;
				}

				// Both setup paths reset the state themselves.
				if (this.isStatic()) {
					this.setupStatic();
				} else {
					this.setupDesktop();
					ScrollTrigger.refresh();
				}
			}, 150);
		},

	};

	scrollAcc.init();
});
